import { useCallback, useEffect, useMemo, useRef } from 'react';
import Vec2 from 'vec2';

import { useChipMinimizerContext } from '../../chipMinimizerContext';
import { useDrawingShapeContext } from '../../drawingShapeContext';
import { useCanvasUpdateHandler } from '../../fabricCanvasContext/hooks';
import { useFaucetContext } from '../../faucetsContext';
import { useOptionalProductContext } from '../../optionalProductContext';
import { usePricedProductsContext } from '../../pricedProductsContext';
import { useProductDetailsContext } from '../../productDetailsContext';
import { useUndoStackContext } from '../../undoStackContext';
import {
  createDShapeFromCloneEstimateShape,
  refactorModelToApplianceModel,
} from './useRestoreEstimateData.utils';

import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { FAUX_SHAPE_INDEX } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import useAppliance from '~/core/hooks/useAppliance';
import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import {
  DrawingShape,
  Estimate,
  SelectedItemDetailEntry,
  SelectedItemProperties,
} from '~/core/models';
import { GetEstimateDto } from '~/core/models/save-estimate-dto';
import { isValidZipCode } from '~/core/utils/validate';
import { useStoresContext } from '~/legacy/easy-estimator-page/context/storesContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectRestorableColorId,
  selectSelectedColorId,
  updateRestorableColorId,
  updateSelectedColorId,
} from '~/redux/slices/color';
import { setCookingAppliance } from '~/redux/slices/cookingAppliance';
import { setCustomCutouts } from '~/redux/slices/cutout';
import {
  getCutoutsFromEstimate,
  getCutoutsFromShapes,
  mergeAllCutouts,
} from '~/redux/slices/cutout/cutout.utils';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { updateApplySameEdge } from '~/redux/slices/edge';
import { selectIsNewEstimate } from '~/redux/slices/estimate';
import {
  selectSelectedMaterial,
  updateRestorableMaterial,
  updateSelectedMaterial,
} from '~/redux/slices/material';
import { updateSelectedPlumbing } from '~/redux/slices/plumbing/plumbing.slice';
import {
  selectSelectedDrawingObjectName,
  updateSelectedDrawingObjectName,
  updateSelectedDrawingTab,
} from '~/redux/slices/shape';
import { setSelectedSinks } from '~/redux/slices/sink';
import { enableAllSteps } from '~/redux/slices/stepper';
import { updateRestorableStoreId } from '~/redux/slices/store';
import { updateSelectedTearOuts } from '~/redux/slices/tearOut';
import { visitAllSteps } from '~/redux/slices/visitedSteps';
import { updateZipcode } from '~/redux/slices/zipcode';
import type { AppDispatch } from '~/redux/store';

/**
 * Restore "+ Add" sink and cooktop/range selections from a saved estimate.
 *
 * Sinks (program, in-store, and customer-supplied) and the cooking-appliance `onCard` maps are the
 * side-panel selections. Drawing/D&D sinks are reconstructed from drawingShapes separately,
 * and each cooking appliance's `onShape` is reconciled from the restored drawingShapes by the
 * `syncCookingAppliancesCount()` effect near the end of this hook, which re-fires after
 * `updateDrawingShapes` updates `drawingShapes` (it sets only onShape, preserving the onCard restored here).
 *
 * `estimateData` is deserialized JSON with no runtime validation. `selectedSinks` and each
 * cooking-appliance sub-field are typed as required but may be absent on older/partial/malformed
 * payloads (e.g. estimates saved before SD-778). Each field is guarded individually so a missing
 * one is skipped rather than dispatched — dispatching `undefined` would either throw
 * (setSelectedSinks spreads its payload) or silently reset that cooking type to empty
 * (setCookingAppliance falls back to the empty initial state). These guards are load-bearing.
 */
const restoreAddButtonSelections = (
  dispatch: AppDispatch,
  estimateData: Estimate
): void => {
  if (estimateData.selectedSinks?.length) {
    dispatch(setSelectedSinks(estimateData.selectedSinks));
  }

  if (estimateData.selectedCookingAppliances) {
    const { selectedCooktopApplianceProduct, selectedRangeApplianceProduct } =
      estimateData.selectedCookingAppliances;

    if (selectedCooktopApplianceProduct) {
      dispatch(
        setCookingAppliance({
          selectedType: 'selectedCooktopApplianceProduct',
          selectedAppliances: selectedCooktopApplianceProduct,
        })
      );
    }
    if (selectedRangeApplianceProduct) {
      dispatch(
        setCookingAppliance({
          selectedType: 'selectedRangeApplianceProduct',
          selectedAppliances: selectedRangeApplianceProduct,
        })
      );
    }
  }
};

export const useRestoreEstimateData = () => {
  const selectedMaterial = useSelectedMaterial();

  const restorableColorId = useAppSelector(selectRestorableColorId);
  const selectedColorId = useAppSelector(selectSelectedColorId);
  const currentMaterial = useAppSelector(selectSelectedMaterial);
  const selectedDrawingObjectName = useAppSelector(
    selectSelectedDrawingObjectName
  );
  const isNewEstimate = useAppSelector(selectIsNewEstimate);

  const dispatch = useAppDispatch();

  const {
    getters: { activeDrawingShape, activeDrawingItem },
    handlers: {
      updateDrawingShapes,
      updateActiveDrawingShapeId,
      resetDrawingShapes,
    },
  } = useDrawingShapeContext();
  const { clearUndoStack } = useUndoStackContext();
  const { resetAllPricing } = usePricedProductsContext();
  const { updateRestorableFaucets } = useFaucetContext();
  const { setRestorableChipMinimizers } = useChipMinimizerContext();
  const {
    setRestorableOptionalProducts,
    restorableOptionalProducts,
    optionalProducts,
  } = useOptionalProductContext();
  const { setRestorableCatalog } = useProductDetailsContext();
  // Restore clears the restorable-catalog snapshot instead of seeding it from
  // the saved estimate: a seeded snapshot could resolve foreign saved ancillary
  // ids client-side and mask them in the merged catalog, which is rebuilt from
  // the saved store's live data instead.
  const resetRestorableCatalog = useCallback(
    () => setRestorableCatalog({}),
    [setRestorableCatalog]
  );
  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();
  const selectedStore = useSelectedStore(); // lead-biased: resolves to the saved store on reopen
  // Shared dedupe ref from the stores context — the auto-commit watcher in
  // useStoreSelection also reads/writes this ref. By setting it here before
  // firing the restore one-shot, we prevent the watcher from firing a second
  // reconcile for the same saved store on estimate reopen.
  const {
    getters: { lastReconciledStoreIdRef },
  } = useStoresContext();

  const { updateShapesDimensions } = useCanvasUpdateHandler();
  const { syncCookingAppliancesCount } = useAppliance();

  const hasRestoredEstimateData = useRef<boolean>(isNewEstimate);
  const hasReconciledOnRestore = useRef(false);

  const buildShapeListFromSavedEstimate = useCallback(
    (estimate: Estimate): Array<DrawingShape> => {
      const dShapes: Array<DrawingShape> = [];

      [...(estimate.drawingShapes ?? [])]?.forEach((shape, idx) => {
        if (idx === FAUX_SHAPE_INDEX) {
          // restore faux shape to get appliances out in the middle of the kitchen again
          const fShape = drawingTool.buildFauxShape();

          if (shape.appliances?.length) {
            const refactoredAppliances = refactorModelToApplianceModel(
              shape.appliances
            );

            const restoredAppliances = refactoredAppliances.map(
              (appliance) => ({
                ...appliance,
                centerPoint: new Vec2(
                  appliance.centerPoint.x,
                  appliance.centerPoint.y
                ),
              })
            );

            fShape.appliances = [...restoredAppliances];
          }
          dShapes.push(fShape);
        } else {
          if (!shape.skeleton?.length) {
            return;
          }

          dShapes.push(createDShapeFromCloneEstimateShape(shape));
        }
      });

      return dShapes;
    },
    []
  );

  const restoreFromFetchedEstimate = useCallback(
    (estimate: GetEstimateDto): void => {
      const estimateData = estimate.estimateData;

      // Zip must precede store/color dispatches; downstream hooks read the zip
      // selector at call time and would fetch with a stale zip otherwise.
      // Saved estimates may have an empty-string zip (treated as unset); fall back to customerZip.
      const resolvedZip = estimateData?.zipCode || estimate.customerZip;

      if (isValidZipCode(resolvedZip)) {
        dispatch(updateZipcode(resolvedZip));
      }

      // This could go away and just read from estimate directly
      dispatch(updateRestorableStoreId(estimate.storeId));

      //TODO: move this to a redux slice
      clearUndoStack();
      resetDrawingShapes();
      resetAllPricing();

      dispatch(
        updateRestorableMaterial({
          description: estimateData.materialDesc,
          qteGrpID: estimateData.qteGrpID,
        })
      );

      if (estimateData.tearOuts) {
        const tearOuts = Object.entries(estimateData.tearOuts).reduce<
          Record<number, number>
        >((acc, [id, value]) => ({ ...acc, [id]: value.quantity }), {});

        dispatch(updateSelectedTearOuts(tearOuts));
      }

      if (estimateData.cutOuts) {
        const cutoutsOnShapes = getCutoutsFromShapes(estimateData);
        const cutoutsOnCards = getCutoutsFromEstimate(estimateData);
        const allCutouts = mergeAllCutouts(cutoutsOnCards, cutoutsOnShapes);

        dispatch(setCustomCutouts(allCutouts));
      }

      if (estimateData.chipMinimizers) {
        setRestorableChipMinimizers(estimateData.chipMinimizers);
      }

      if (estimateData.optionalProducts) {
        for (const [key, item] of Object.entries(
          estimateData.optionalProducts
        )) {
          const keyNum = parseInt(key);

          if (item.quantity > 0 && item.note && !item.detailEntries?.length) {
            const entry: SelectedItemDetailEntry = {
              quantity: item.quantity,
              note: item.note,
            };
            const updatedItem: SelectedItemProperties = {
              ...item,
              detailEntries: [entry],
            };

            estimateData.optionalProducts[keyNum] = updatedItem;
          }
        }

        if (Object.keys(estimateData.optionalProducts).length > 0) {
          setRestorableOptionalProducts(estimateData.optionalProducts);
        }
      }

      if (estimateData.faucets) {
        updateRestorableFaucets(estimateData.faucets);
      }

      if (estimateData.plumbingItems) {
        dispatch(updateSelectedPlumbing(estimateData.plumbingItems));
      }

      restoreAddButtonSelections(dispatch, estimateData);

      if (estimateData.color) {
        dispatch(updateRestorableColorId(estimateData.color.productId));
      }

      resetRestorableCatalog();

      dispatch(updateApplySameEdge(estimateData.applySameEdge));

      const _drawingShapes = buildShapeListFromSavedEstimate(estimateData);

      const drawingShapes = [...drawingTool.cloneShapes(_drawingShapes)].map(
        (o) => Object.assign(Object.create(Object.getPrototypeOf(o)), o)
      );

      updateShapesDimensions({
        color: estimateData.color,
        drawingShapes,
      });

      updateDrawingShapes([...drawingShapes]);
      if (activeDrawingShape?.id !== drawingShapes[FAUX_SHAPE_INDEX + 1]?.id) {
        updateActiveDrawingShapeId(drawingShapes[FAUX_SHAPE_INDEX + 1].id);
      }

      dispatch(updateNeedRedrawCanvas(true));
      dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
    },
    [
      dispatch,
      activeDrawingShape?.id,
      buildShapeListFromSavedEstimate,
      clearUndoStack,
      resetAllPricing,
      resetDrawingShapes,
      resetRestorableCatalog,
      setRestorableChipMinimizers,
      setRestorableOptionalProducts,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
      updateRestorableFaucets,
      updateShapesDimensions,
    ]
  );

  const isEstimateDataFullyRestored = useMemo(
    () =>
      restorableColorId === selectedColorId &&
      selectedMaterial?.id === currentMaterial?.id &&
      activeDrawingItem?.type === 'shape' &&
      (activeDrawingItem?.item as DrawingShape)?.label ===
        selectedDrawingObjectName,
    [
      activeDrawingItem?.item,
      activeDrawingItem?.type,
      currentMaterial?.id,
      restorableColorId,
      selectedColorId,
      selectedDrawingObjectName,
      selectedMaterial?.id,
    ]
  );

  const restoreLoadedEstimateData = useCallback(() => {
    if (restorableColorId && restorableColorId !== selectedColorId) {
      dispatch(updateSelectedColorId(restorableColorId));
    }

    if (selectedMaterial && selectedMaterial.id !== currentMaterial?.id) {
      dispatch(updateSelectedMaterial(selectedMaterial));
    }

    if (activeDrawingItem?.type === 'shape') {
      const activeDrawingShapeLabel = (activeDrawingItem?.item as DrawingShape)
        ?.label;

      if (
        activeDrawingShapeLabel &&
        activeDrawingShapeLabel !== selectedDrawingObjectName
      ) {
        dispatch(updateSelectedDrawingObjectName(activeDrawingShapeLabel));
      }
    }

    // Force canvas redraw after estimate restoration to ensure proper rendering
    setTimeout(() => {
      dispatch(updateNeedRedrawCanvas(true));
    }, 200);
  }, [
    activeDrawingItem?.item,
    activeDrawingItem?.type,
    currentMaterial?.id,
    dispatch,
    restorableColorId,
    selectedColorId,
    selectedDrawingObjectName,
    selectedMaterial,
  ]);

  useEffect(() => {
    if (!isNewEstimate && !hasRestoredEstimateData.current) {
      restoreLoadedEstimateData();
      if (isEstimateDataFullyRestored) {
        hasRestoredEstimateData.current = true;
        dispatch(enableAllSteps());
        dispatch(visitAllSteps());
      }
    }
  }, [
    dispatch,
    isEstimateDataFullyRestored,
    isNewEstimate,
    restoreLoadedEstimateData,
  ]);

  useEffect(() => {
    syncCookingAppliancesCount();
  }, [syncCookingAppliancesCount]);

  // Reset the one-shot reconcile flag when a new estimate begins.
  useEffect(() => {
    if (isNewEstimate) {
      hasReconciledOnRestore.current = false;
    }
  }, [isNewEstimate]);

  // Fire a one-shot post-restore reconcile against the saved store.
  // Gated on a bounded readiness condition: proceed once the optional staging
  // map (restorableOptionalProducts — the optional selections captured from the
  // saved estimate, waiting for the optional catalog to load so the promotion
  // effect can move them into the live selection) has drained, OR the
  // saved-store optional catalog has loaded. The OR (not AND) prevents a
  // permanent block when the store's optional catalog is empty/late — the
  // promotion effect would never fire to drain the staging map. The guarded
  // reset in replaceProductsWithEquivalents ensures firing here cannot wipe a
  // still-staged optional map.
  //
  // selectedStore is the lead-biased value — it resolves to the saved store on
  // reopen — so this reconciles ancillaries against the saved store's live
  // catalog (drops/remaps any foreign ids) without changing the selected store.
  useEffect(() => {
    if (
      isNewEstimate ||
      !hasRestoredEstimateData.current ||
      hasReconciledOnRestore.current ||
      !selectedStore?.companyId
    ) {
      return;
    }
    const isOptionalStagingMapDrained =
      Object.keys(restorableOptionalProducts).length === 0;
    const isOptionalCatalogLoaded = optionalProducts.length > 0;

    if (isOptionalStagingMapDrained || isOptionalCatalogLoaded) {
      hasReconciledOnRestore.current = true;
      // Mark the saved store reconciled in the shared ref BEFORE firing, so the
      // auto-commit watcher (useStoreSelection) sees it already done and skips a
      // redundant second reconcile when Prong-2 write-back changes the raw Redux
      // selectedStore from undefined → saved store on reopen.
      lastReconciledStoreIdRef.current = selectedStore.companyId;
      replaceProductsWithEquivalents({
        storeId: selectedStore.companyId,
      }).catch(() => {
        /* best-effort */
      });
    }
  }, [
    isNewEstimate,
    restorableOptionalProducts,
    optionalProducts,
    selectedStore?.companyId,
    replaceProductsWithEquivalents,
    lastReconciledStoreIdRef,
  ]);

  return { restoreFromFetchedEstimate };
};
